'use client';

import { Suspense, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { HeroGenerator, type SubmittedInfo } from '@/components/studio/HeroGenerator';
import { ModelExamples } from '@/components/studio/ModelExamples';
import { SuggestedVideos } from '@/components/studio/SuggestedVideos';
import { StudioStage } from '@/components/studio/StudioStage';

type GenerationType = 'image' | 'video';

function resolveInitialType(searchParams: URLSearchParams | null): GenerationType {
  const t = searchParams?.get('type');
  if (t === 'image' || t === 'video') return t;
  const legacy = searchParams?.get('tab');
  if (legacy === 'image' || legacy === 'video') return legacy;
  return 'video';
}

function StudioGeneratorContent() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [type, setType] = useState<GenerationType>(() =>
    resolveInitialType(searchParams)
  );
  const [prompt, setPrompt] = useState(
    () => searchParams?.get('prompt') ?? ''
  );
  // The job that owns the stage under the composer. A generation stays on
  // this page from submit through result — the library is a history view,
  // not a waiting room.
  const [job, setJob] = useState<{ id: string; info?: SubmittedInfo } | null>(null);
  const [submitToken, setSubmitToken] = useState(0);
  const [jobPending, setJobPending] = useState(false);

  // Keep URL in sync with type so deep links work
  useEffect(() => {
    const current = searchParams?.get('type');
    if (current !== type) {
      const params = new URLSearchParams(searchParams?.toString() ?? '');
      params.set('type', type);
      router.replace(`/studio?${params.toString()}`, { scroll: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Fill the composer from a suggested example and scroll the field back into
  // view — the input lives at the top of the page (and inside the studio's
  // own lenis-prevented scroll container), so without this the user taps
  // "use prompt" and never sees where it landed.
  const usePrompt = (value: string) => {
    setPrompt(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document
      .querySelector<HTMLElement>('[data-lenis-prevent]')
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      data-lenis-prevent
      className="relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
    >
      <section className="px-4 pt-8 sm:px-6 sm:pt-[47px]">
        <div className="mx-auto w-full max-w-[772px]">
          <HeroGenerator
            type={type}
            onTypeChange={setType}
            promptValue={prompt}
            onPromptChange={setPrompt}
            submitToken={submitToken}
            busy={jobPending}
            onSubmitted={(jobId, info) => setJob({ id: jobId, info })}
          />

          {job && (
            /* 30px here + the composer's own 12px bottom margin = the 42px
               gap the design puts between composer and stage. */
            <div className="mt-[30px]">
              <StudioStage
                jobId={job.id}
                info={job.info}
                onRegenerate={() => setSubmitToken((n) => n + 1)}
                onDismiss={() => {
                  setJob(null);
                  setJobPending(false);
                }}
                onPendingChange={setJobPending}
              />
            </div>
          )}
        </div>
      </section>

      {/* Empty-state filler: our curated suggestions (video only) sit above
          the provider showcase — their prompts are written for station ads
          and drop into the composer in one tap. Once a job is on the stage
          both blocks would only compete with it, so they hide entirely. */}
      {!job && (
        <>
          {type === 'video' && (
            <section className="w-full px-4 pt-[60px] sm:px-6">
              <div className="mx-auto w-full max-w-[1120px]">
                <SuggestedVideos onUsePrompt={usePrompt} />
              </div>
            </section>
          )}
          <section
            className={`w-full px-4 pb-[60px] sm:px-6 ${
              type === 'video' ? 'pt-10' : 'pt-[60px]'
            }`}
          >
            <div className="mx-auto w-full max-w-[1120px]">
              <ModelExamples type={type} />
            </div>
          </section>
        </>
      )}
      {job && <div className="h-[60px] shrink-0" />}
    </div>
  );
}

export default function StudioPage() {
  return (
    <Suspense fallback={null}>
      <StudioGeneratorContent />
    </Suspense>
  );
}
